import { hueFrom } from './format';

/**
  * Average colour of a cover image, used to tint the player's ambient glow.
  *
  * Sampling the artwork rather than hashing the title is what makes the player
  * feel like it belongs to the book you are listening to. Drive covers come
  * through the service worker on our own origin and demo covers are data URIs,
  * so the canvas is never tainted; anything that does taint falls back to a hue
  * derived from the title rather than failing.
  */

const cache = new Map<string, string>();

/** An HSL triplet suitable for `hsl(<value>)`, e.g. "232 70% 60%". */
export function fallbackAccent(seed: string): string {
    return `${hueFrom(seed)} 62% 58%`;
}

export async function accentFromImage(url: string, seed: string): Promise<string> {
    const cached = cache.get(url);
    if (cached) return cached;

    try {
      const accent = await sample(url);
      cache.set(url, accent);
      return accent;
    } catch {
      const accent = fallbackAccent(seed);
      cache.set(url, accent);
      return accent;
    }
}

function sample(url: string): Promise<string> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.decoding = 'async';

      img.onerror = () => reject(new Error('cover failed to load'));
      img.onload = () => {
        // 12x12 is enough to average a cover and costs nothing to draw.
        const size = 12;
        const canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;

        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        if (!ctx) return reject(new Error('no 2d context'));

        ctx.drawImage(img, 0, 0, size, size);

        let data: Uint8ClampedArray;
        try {
          data = ctx.getImageData(0, 0, size, size).data;
        } catch {
          // Tainted canvas — cross-origin image without CORS headers.
          return reject(new Error('tainted canvas'));
        }

        let r = 0;
        let g = 0;
        let b = 0;
        let weight = 0;

        for (let i = 0; i < data.length; i += 4) {
          const [pr, pg, pb] = [data[i]!, data[i + 1]!, data[i + 2]!];
          const max = Math.max(pr, pg, pb);
          const min = Math.min(pr, pg, pb);
          // Weight by saturation so a mostly-grey cover does not average to mud.
          const w = 0.25 + (max - min) / 255;
          r += pr * w;
          g += pg * w;
          b += pb * w;
          weight += w;
        }

        if (weight === 0) return reject(new Error('empty image'));
        resolve(toAccentHsl(r / weight, g / weight, b / weight));
      };

      img.src = url;
    });
}

/** Normalises an arbitrary average into a hue that works as a soft glow:
  * keeps the hue, forces saturation and lightness into a usable band. */
function toAccentHsl(r: number, g: number, b: number): string {
   const rn = r / 255;
   const gn = g / 255;
   const bn = b / 255;
   const max = Math.max(rn, gn, bn);
   const min = Math.min(rn, gn, bn);
   const delta = max - min;

   let hue = 0;
   if (delta !== 0) {
     if (max === rn) hue = ((gn - bn) / delta) % 6;
     else if (max === gn) hue = (bn - rn) / delta + 2;
     else hue = (rn - gn) / delta + 4;
   }
   hue = Math.round(((hue * 60) % 360 + 360) % 360);

   const lightness = (max + min) / 2;
   const saturation = delta === 0 ? 0 : delta / (1 - Math.abs(2 * lightness - 1));

   const s = Math.round(Math.min(0.78, Math.max(0.42, saturation)) * 100);
   return `${hue} ${s}% 58%`;
}
